import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { provideHttpClient, withJsonpSupport } from "@angular/common/http";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import {
    AccessService,
    AiTsrService,
    AppService,
    AuthGuard,
    AuthUserService,
    CalculationService,
    CommonService,
    ContainerService,
    DeactivateGuard,
    EditorService,
    FontLoaderService,
    LocalStoreService,
    ReversalAuthGuard,
    SceneAddService,
    SceneService,
    StoreSelectorsService,
    StyleService,
    UserService,
} from "./services";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import {
    convertNumberToBoolean,
    HOME_URL,
    isGalaxy,
    isHostFeatures,
    isHostLocal,
    jsDateToEpoch,
    LOGIN_URL,
    TIME_RANGE_SEPARATOR,
    isSkipLoadingDataRoute
} from "./common";
import { MainModule } from "./layouts";
import { NotFoundModule, StartModule } from "./containers";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { appReducers } from "./store/reducers/app.reducers";
import { UserEffects } from "./store/effects/user.effects";
import { EffectsModule } from "@ngrx/effects";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
    faDropbox,
    faFacebook,
    faGoogleDrive,
    faGooglePlus,
    faInstagram,
    faLinkedin,
    faPinterest,
    faReddit,
    faTelegram,
    faTiktok,
    faTwitter,
    faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { environment } from "../environments/environment";
import { BehaviorSubject } from "rxjs";
import { MatDialogModule } from "@angular/material/dialog";
import { SharedModule } from "./common/shared.module";
import { ConfigEffects } from "./store/effects/config.effects";
import { TwitterService } from "./services/twitter-service";
import { ToastrModule } from "ngx-toastr";
import { EnterpriseService } from "./containers/enterprise/services/enterprise-service";
import { EnterpriseEffects } from "./store/effects/enterprise.effects";
import { ServiceWorkerModule } from "@angular/service-worker";
import { GoogleMap, GoogleMapsModule } from "@angular/google-maps";
import { GoogleLoginProvider, SocialAuthServiceConfig, SocialLoginModule } from "@abacritt/angularx-social-login";
import { BlockLabels } from "./containers/editor/scene-blocks/block-constants";
import { actionAppSettings } from "./store/actions/user.actions";
import { TranslocoRootModule } from "./transloco-root.module";
import { FasterqEffects } from "./store/effects/fasterq.effects";
import { GlobalErrorHandler } from "./services/global.error.handler-service";
import { FasterqServices } from "./containers/fasterq/services/fasterq-services";
import { OverlayModule } from "@angular/cdk/overlay";
import { StringJSV2Pipe } from "./pipes/stringjs-pipe";
import { PwaPromptService } from "./services/pwa-prompt.service";
import { IAppStatic } from "./interfaces";
import { take } from "rxjs/operators";
import { Lib } from "../libs/Lib";
import "gsap";
import * as Draggable from "gsap/Draggable";
import "gsap/TweenLite";
// Ensure Draggable is loaded (suppress unused warning)
void Draggable;
import * as _ from "lodash";
import { HIGHLIGHT_OPTIONS } from "ngx-highlightjs";

// import {SecureService} from "./services/secure-service";
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggleDefaultOptions } from "@angular/material/button-toggle";

/** @default: true
 if set to false manually to false, please remember to set back to true before git submit
 **/

const SHOW_GLOBAL_ERRORS = true;

export function globalErrHandle() {
    if (!SHOW_GLOBAL_ERRORS) return { provide: ErrorHandler, useClass: null };
    if (!isHostLocal()) return { provide: ErrorHandler, useClass: GlobalErrorHandler };
    const timeNow = jsDateToEpoch(new Date());
    const quietUntilStored = Number(window.localStorage.getItem("ignoreErrorsTimes"));
    if (timeNow < quietUntilStored) return { provide: ErrorHandler, useClass: null };
    return { provide: ErrorHandler, useClass: GlobalErrorHandler };
}

export function provideSocialConfig() {
    if (isHostLocal()) {
        // Provide a dummy Google provider for local development to prevent NG0201 error
        return [
            {
                id: GoogleLoginProvider.PROVIDER_ID,
                provider: new GoogleLoginProvider("dummy-client-id"),
            },
        ];
    }
    return [
        {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider("637219265100-r4jh7dppmcs1so7mr1jpvffmskke8mrm.apps.googleusercontent.com"),
        },
    ];
}

let localImports = [
    BrowserAnimationsModule,
    BrowserModule,
    AppRoutingModule,
    MainModule,
    TranslocoRootModule,
    GoogleMapsModule,
    MatDialogModule,
    StartModule,
    NotFoundModule,
    FontAwesomeModule,
    OverlayModule,
    SocialLoginModule,
    ToastrModule.forRoot({
        enableHtml: true,
        progressBar: true,
        preventDuplicates: !Lib.DevMode(),
    }),
    StoreModule.forRoot(appReducers, {
        runtimeChecks: {
            strictStateImmutability: false,
            strictActionImmutability: false,
            strictStateSerializability: false,
            strictActionSerializability: false,
        },
    }),
    SharedModule.forRoot(),
    EffectsModule.forRoot([UserEffects, EnterpriseEffects, ConfigEffects, FasterqEffects]),
    StoreRouterConnectingModule.forRoot(),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    ServiceWorkerModule.register("service-worker.web.js", {
        enabled: true,
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: "registerWithDelay:3000",
    }),
];

const initializer = (pwaService: PwaPromptService) => () => {
    // Skip initialization for skipped routes
    if (isSkipLoadingDataRoute()) {
        return Promise.resolve();
    }
    return pwaService.initPwaPrompt();
};
const defaultButtonToggleOptions: MatButtonToggleDefaultOptions = {
    hideMultipleSelectionIndicator: true,
    hideSingleSelectionIndicator: true,
};

@NgModule({
    declarations: [AppComponent],
    imports: localImports,
    providers: [
        GoogleMap,
        StringJSV2Pipe,
        AuthGuard,
        CalculationService,
        AccessService,
        TwitterService,
        EditorService,
        ContainerService,
        CommonService,
        FontLoaderService,
        UserService,
        LocalStoreService,
        EnterpriseService,
        FasterqServices,
        DeactivateGuard,
        ReversalAuthGuard,
        StyleService,
        SceneAddService,
        AppService,
        StoreSelectorsService,
        AuthUserService,
        SceneService,
        AiTsrService,
        GlobalErrorHandler,
        provideHttpClient(
            withJsonpSupport(),
        ),
        {
            provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
            useValue: defaultButtonToggleOptions,
        },
        globalErrHandle(),
        // SecureService, // enable to test service
        // ChecklistDatabaseService,
        {
            provide: APP_INITIALIZER,
            useFactory: initializer,
            deps: [PwaPromptService],
            multi: true,
        },
        {
            provide: HOME_URL,
            useValue: "",
        },
        {
            provide: 'SocialAuthServiceConfig',
            useValue: {
                autoLogin: false,
                providers: provideSocialConfig(),
                onError: (err) => {
                    if (isGalaxy()) {
                        console.error(err);
                    }
                },
            } as SocialAuthServiceConfig,
        },
        {
            provide: LOGIN_URL,
            useValue: "login",
        },
        {
            provide: TIME_RANGE_SEPARATOR,
            useValue: " - ",
        },
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                coreLibraryLoader: () => import("highlight.js/lib/core"),
                lineNumbersLoader: () => import("ngx-highlightjs/line-numbers"),
                languages: {
                    typescript: () => import("highlight.js/lib/languages/typescript"),
                    javascript: () => import("highlight.js/lib/languages/javascript.js"),
                    css: () => import("highlight.js/lib/languages/css"),
                    xml: () => import("highlight.js/lib/languages/xml"),
                },

                // fullLibraryLoader: () => import('highlight.js'),
            },
        },

        // ,{
        //     provide: ErrorHandler,
        //     useClass: GlobalErrorHandler
        // }
        // {
        //     provide: HTTP_INTERCEPTORS,
        //     useClass: AuthInterceptor,
        //     multi: true
        // },
        // {
        //     provide: HTTP_INTERCEPTORS,
        //     useClass: ErrorResponseInterceptor,
        //     multi: true
        // }
    ],
    exports: [],
    bootstrap: [AppComponent],
})

export class AppModule {
    // force rxjs workaround
    readonly bs: BehaviorSubject<any> = new BehaviorSubject({});

    constructor(private library: FaIconLibrary,
                private globalErrorHandler: GlobalErrorHandler,
                private storeSelector: StoreSelectorsService,
                private localStoreService: LocalStoreService) {

        // Skip all initialization for auth-callback route
        if (isSkipLoadingDataRoute()) {
            return;
        }

        this.storeSelector.blockLabels = BlockLabels;

        // Add social media brand icons to FontAwesome library
        library.addIcons(
            faDropbox, faFacebook, faGoogleDrive, faGooglePlus, faInstagram,
            faLinkedin, faPinterest, faReddit, faTelegram, faTiktok, faTwitter, faYoutube,
        );

        storeSelector.getAppSettings().pipe(take(1))
            .subscribe((appSettings: IAppStatic) => {
                /** we show all features if utl has /features or if we are running in localhost **/
                const saveFullFeature = convertNumberToBoolean(this.localStoreService.getStorageValConfig("saveFullFeature"));
                if (isHostFeatures() || isHostLocal() || saveFullFeature) {
                    const appData = _.cloneDeep(appSettings);
                    ///////////////////////////////////////////////
                    /** override to value of [false] to see what real customers will see, default should be set to [true] **/
                    appData.isFullFeatures = true;
                    ///////////////////////////////////////////////
                    console.log("> full featured mode");
                    this.storeSelector.dispatch(actionAppSettings({ appData }));
                }
            });
    }
}

// ServiceWorkerModule.register('ngsw-worker.js', {enabled: environment.production, registrationStrategy: 'registerWhenStable:5000'})
// if (Lib.DevMode()) {
//     localImports = localImports.filter((v) => v !== SocialLoginModule);
// }

// overlayContainer.getContainerElement().classList.add('app-theme-dark');
// overlayContainer.getContainerElement().classList.add('app-theme-light');

// // if (isGalaxy() && !isHostFeatures()) return [];
// // if (isHostDev()) return [];
// // if (isHostLocal()) return [];
// if (isHostFeatures()) {
//     console.log('> loaded social module');
//     return [
//         {
//             id: GoogleLoginProvider.PROVIDER_ID,
//             provider: new GoogleLoginProvider("637219265100-r4jh7dppmcs1so7mr1jpvffmskke8mrm.apps.googleusercontent.com")
//         }
//         // ,
//         // {
//         //     id: FacebookLoginProvider.PROVIDER_ID,
//         //     provider: new FacebookLoginProvider('608495059928829')
//         // }
//     ];
// } else {
//     return [];
// }


